/**
 * App-permission checks against the context Django renders into the page.
 *
 * `extend_context_for_user` (TECWeb/TECCore/view_master.py) puts the employee's permission keys
 * on both `authContext.employee.permissions` and `authContext.permissions`, and a superuser
 * bypasses the check — the same rule the `@app_permission_required` decorator applies
 * server-side. Both sources are read here so callers cannot drift apart on which one wins.
 */

export interface PermissionContext {
  authContext?: {
    user?: { is_superuser?: boolean } | null;
    employee?: { permissions?: unknown } | null;
    permissions?: unknown;
  } | null;
}

/** True when the context holds the given app permission, or belongs to a superuser. */
export function hasAppPermission(
  context: PermissionContext | null | undefined,
  permission: string,
): boolean {
  const auth = context?.authContext;
  if (auth?.user?.is_superuser) {
    return true;
  }

  const fromEmployee = auth?.employee?.permissions;
  const permissions: unknown[] = Array.isArray(fromEmployee)
    ? fromEmployee
    : Array.isArray(auth?.permissions)
      ? (auth?.permissions as unknown[])
      : [];

  return permissions.includes(permission);
}

/**
 * True for users who can see the Quality Executive dashboard.
 *
 * This is the gate for quality-only behaviour such as the "All facilities" login choice.
 * It is deliberately the per-user permission rather than the tenant's `client_type`: EMR
 * tenants grant `quality_dashboard` too, and `client_type` cannot vary per user (issue #243).
 */
export function canViewQualityDashboard(context: PermissionContext | null | undefined): boolean {
  return hasAppPermission(context, 'quality_dashboard');
}
